import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import {
  getIsRescissionApplicable,
  getProjectedMaturityTermMonths,
} from 'app/pages/loanApplication/tabs/loanClosingCDS/EditDrawer/Content/Content.utils';
import _ from 'lodash';

import { PAGE_CODES } from 'constants/pageCodes';
import useCompanyId from 'hooks/useCompanyId';
import { getConditionalArray } from 'utils/data';

import { getWarehouseBanksAction } from 'store/accounting/actions';
import { selectWarehouseBankOptions } from 'store/accounting/selectors';
import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { getBusinessDaysAction } from 'store/companies/actions';
import { selectClosingBusinessDays } from 'store/companies/selectors';
import {
  getChattelDetailsAction,
  getClosingInfoAction,
  getPropertiesActionV2,
  getPropertyAndLoanSummaryAction,
} from 'store/loanApplications/actions';
import {
  selectClosingCompanies,
  selectClosingDatesApplicationData,
  selectClosingInfo,
  selectFundingInfo,
  selectIsChattelLoanFromPropertyAndLoanSummary,
  selectIsDisabledClosingInformationFields,
  selectIsDisabledLockGatedClosingFields,
  selectIsLoanClosingTwoPart,
  selectIsUnsecuredFromPropertyAndLoanSummary,
  selectLoanApplicationSubjectPropertyOccupancy,
  selectLoanApplicationSubjectPropertyState,
  selectMaturityCalcInputs,
} from 'store/loanApplications/selectors';

import {
  buildClosingCompaniesByPrefix,
  buildClosingCompanySelectorOptions,
} from '../../LoanEditorCustomFormDrawer.closing.utils';
import {
  buildClosingApplicationData,
  buildDrawerFundingApplicationData,
} from '../../LoanEditorCustomFormDrawer.closingFunding.save';
import { CLOSING_FORM_PATH, FUNDING_FORM_PATH } from '../../LoanEditorCustomFormDrawer.constants';
import { logRejectedResults } from '../../LoanEditorCustomFormDrawer.save';
import { checkHasClosingItems, checkHasFundingItems } from '../../LoanEditorCustomFormDrawer.utils';

import { resolveClosingFundingLoadState, resolveLiveTimestamp, withLiveDate } from './utils';

const useClosingFundingContexts = ({ loanId, applicationId }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const companyId = useCompanyId();

  const closingInfo = useSelector(selectClosingInfo);
  const closingCompanies = useSelector(selectClosingCompanies);
  const fundingInfo = useSelector(selectFundingInfo);
  const warehouseBankOptions = useSelector(selectWarehouseBankOptions);
  const isFieldsDisabled = useSelector(selectIsDisabledClosingInformationFields);
  const isLockGatedFieldsDisabled = useSelector(selectIsDisabledLockGatedClosingFields);
  const isTwoPartClosing = useSelector(selectIsLoanClosingTwoPart);
  const isUnsecured = useSelector(selectIsUnsecuredFromPropertyAndLoanSummary);
  const isChattel = !!useSelector(selectIsChattelLoanFromPropertyAndLoanSummary);
  const businessDays = useSelector(selectClosingBusinessDays);
  const loanApplication = useSelector(selectClosingDatesApplicationData);
  const subjectPropertyOccupancy = useSelector(selectLoanApplicationSubjectPropertyOccupancy);
  const subjectPropertyState = useSelector(selectLoanApplicationSubjectPropertyState);
  const maturityCalcInputs = useSelector(selectMaturityCalcInputs);
  const { isViewAllowed, isEditAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_CLOSING),
  );

  const [isClosingLoaded, setIsClosingLoaded] = useState(false);
  const [isFundingLoaded, setIsFundingLoaded] = useState(false);
  const [selectedClosingCompanyPrefix, setSelectedClosingCompanyPrefix] = useState(null);
  const [liveClosingDates, setLiveClosingDates] = useState({});
  const [isProgramLoaded, setIsProgramLoaded] = useState(false);

  const loadClosingFundingData = useCallback(
    async sections => {
      setIsClosingLoaded(false);
      setIsFundingLoaded(false);
      setSelectedClosingCompanyPrefix(null);
      setLiveClosingDates({});
      setIsProgramLoaded(false);

      const hasLoan = !!isViewAllowed && !_.isNil(loanId);
      const shouldLoadClosing = hasLoan && checkHasClosingItems(sections);
      const shouldLoadFunding = hasLoan && checkHasFundingItems(sections);

      if (!shouldLoadClosing && !shouldLoadFunding) {
        return;
      }

      // The store keeps the last loan's summary until this lands, and the chattel check below reads
      // it, so it loads first. A failure still loads the closing fields, but the chattel and
      // unsecured checks keep whatever summary the store holds.
      const programResults = await Promise.allSettled([
        dispatch(getPropertyAndLoanSummaryAction({ applicationId })),
      ]);
      setIsProgramLoaded(!logRejectedResults(programResults).length);
      const isChattelLoan = selectIsChattelLoanFromPropertyAndLoanSummary(store.getState());

      const [closingInfoResults, closingResults, fundingResults] = await Promise.all([
        Promise.allSettled([dispatch(getClosingInfoAction({ loanId }))]),
        Promise.allSettled(
          getConditionalArray(shouldLoadClosing, () => [
            dispatch(getBusinessDaysAction({ companyId })),
            dispatch(getPropertiesActionV2({ applicationId, withSubjectProperty: true })),
            ...getConditionalArray(isChattelLoan, () => [
              dispatch(getChattelDetailsAction({ applicationId })),
            ]),
          ]),
        ),
        Promise.allSettled(
          getConditionalArray(shouldLoadFunding, () => [dispatch(getWarehouseBanksAction())]),
        ),
      ]);

      logRejectedResults([...closingInfoResults, ...closingResults, ...fundingResults]);

      const loadState = resolveClosingFundingLoadState({
        closingInfoResults,
        closingResults,
        fundingResults,
        shouldLoadClosing,
        shouldLoadFunding,
      });

      setIsClosingLoaded(loadState.isClosingLoaded);
      setIsFundingLoaded(loadState.isFundingLoaded);
    },
    [applicationId, companyId, dispatch, isViewAllowed, loanId, store],
  );

  const setCurrentCloseAt = useCallback(
    closeAt =>
      setLiveClosingDates(dates => withLiveDate({ dates, key: 'closeAt', value: closeAt })),
    [],
  );
  const setCurrentNoteDate = useCallback(
    noteDate =>
      setLiveClosingDates(dates => withLiveDate({ dates, key: 'noteDate', value: noteDate })),
    [],
  );
  const setCurrentFundingDate = useCallback(
    fundingDate =>
      setLiveClosingDates(dates => withLiveDate({ dates, key: 'fundingDate', value: fundingDate })),
    [],
  );

  const prepaidInterestCreditData = closingInfo?.prepaidInterestCreditData;
  const hasInterestCredit = !!prepaidInterestCreditData?.hasInterestCredit;
  const isEntityEditAllowed = !!isEditAllowed && !isFieldsDisabled;

  const companiesByPrefix = useMemo(
    () => buildClosingCompaniesByPrefix(closingCompanies),
    [closingCompanies],
  );

  const closingCompanySelectorOptions = useMemo(
    () => buildClosingCompanySelectorOptions(companiesByPrefix),
    [companiesByPrefix],
  );

  const storedClosingData = useMemo(
    () => buildClosingApplicationData({ closingInfo, closingCompanies }),
    [closingCompanies, closingInfo],
  );

  const storedFundingData = useMemo(
    () => buildDrawerFundingApplicationData(fundingInfo),
    [fundingInfo],
  );

  const closingContext = useMemo(
    () => ({
      isEnabled: isClosingLoaded,
      isViewAllowed: !!isViewAllowed,
      isEditAllowed: isEntityEditAllowed,
      isLockGatedFieldsDisabled,
      isTwoPartClosing,
      isUnsecured,
      isChattel,
      businessDays,
      isRescissionApplicable: getIsRescissionApplicable({
        loanApplication,
        subjectPropertyOccupancy,
      }),
      closeAt: resolveLiveTimestamp({
        liveDates: liveClosingDates,
        key: 'closeAt',
        storedValue: closingInfo?.closeAt,
      }),
      noteDate: resolveLiveTimestamp({
        liveDates: liveClosingDates,
        key: 'noteDate',
        storedValue: prepaidInterestCreditData?.interestCreditNoteDate,
      }),
      fundingDate: resolveLiveTimestamp({
        liveDates: liveClosingDates,
        key: 'fundingDate',
        storedValue: prepaidInterestCreditData?.interestCreditFundingDate,
      }),
      companies: companiesByPrefix,
      selectedCompanyPrefix: selectedClosingCompanyPrefix,
    }),
    [
      businessDays,
      closingInfo?.closeAt,
      companiesByPrefix,
      isChattel,
      isClosingLoaded,
      isEntityEditAllowed,
      isLockGatedFieldsDisabled,
      isTwoPartClosing,
      isUnsecured,
      isViewAllowed,
      liveClosingDates,
      loanApplication,
      prepaidInterestCreditData?.interestCreditFundingDate,
      prepaidInterestCreditData?.interestCreditNoteDate,
      selectedClosingCompanyPrefix,
      subjectPropertyOccupancy,
    ],
  );

  const fundingContext = useMemo(
    () => ({
      isEnabled: isFundingLoaded,
      isViewAllowed: !!isViewAllowed,
      isEditAllowed: isEntityEditAllowed,
      isLockGatedFieldsDisabled,
      isUnsecured,
      warehouseBankOptions,
    }),
    [
      isEntityEditAllowed,
      isFundingLoaded,
      isLockGatedFieldsDisabled,
      isUnsecured,
      isViewAllowed,
      warehouseBankOptions,
    ],
  );

  const closingFundingApplicationData = useMemo(
    () => ({
      ...(isClosingLoaded ? { [CLOSING_FORM_PATH]: storedClosingData } : {}),
      ...(isFundingLoaded ? { [FUNDING_FORM_PATH]: storedFundingData } : {}),
    }),
    [isClosingLoaded, isFundingLoaded, storedClosingData, storedFundingData],
  );

  const closingSaveContext = useMemo(
    () => ({
      storedClosingData,
      closingCompanies,
      hasInterestCredit,
      loanApplication,
      subjectPropertyState,
      businessDays,
      isLockGatedFieldsDisabled,
    }),
    [
      businessDays,
      closingCompanies,
      hasInterestCredit,
      isLockGatedFieldsDisabled,
      loanApplication,
      storedClosingData,
      subjectPropertyState,
    ],
  );

  const maturityTermMonths = useMemo(
    () =>
      getProjectedMaturityTermMonths({
        maturityCalcInputs: isProgramLoaded ? maturityCalcInputs : {},
        firstPaymentDate: prepaidInterestCreditData?.interestCreditFirstPaymentDate,
        maturityDate: closingInfo?.maturityDate,
      }),
    [
      closingInfo?.maturityDate,
      isProgramLoaded,
      maturityCalcInputs,
      prepaidInterestCreditData?.interestCreditFirstPaymentDate,
    ],
  );

  const closingCascadeProps = useMemo(
    () => ({
      hasInterestCredit,
      businessDays,
      loanApplication,
      subjectPropertyOccupancy,
      subjectPropertyState,
      maturityTermMonths,
      isChattel,
      setCurrentCloseAt,
      setCurrentNoteDate,
      setCurrentFundingDate,
    }),
    [
      businessDays,
      hasInterestCredit,
      isChattel,
      loanApplication,
      maturityTermMonths,
      setCurrentCloseAt,
      setCurrentFundingDate,
      setCurrentNoteDate,
      subjectPropertyOccupancy,
      subjectPropertyState,
    ],
  );

  return {
    closingContext,
    fundingContext,
    closingFundingApplicationData,
    closingSaveContext,
    storedFundingData,
    closingCascadeProps,
    closingCompanySelectorOptions,
    selectedClosingCompanyPrefix,
    setSelectedClosingCompanyPrefix,
    loadClosingFundingData,
    isClosingEditable: isClosingLoaded && !!isViewAllowed && isEntityEditAllowed,
    isFundingEditable:
      isFundingLoaded && !!isViewAllowed && isEntityEditAllowed && !isLockGatedFieldsDisabled,
    isClosingLoaded,
    isFundingLoaded,
  };
};

export default useClosingFundingContexts;
